import React, { useState } from 'react';

/**
 * Panel "faktura zawiera pozycje, ktorych nie ma w systemie".
 *
 * Zawsze daje co najmniej jedno wyjscie dalej - kasjer nie moze utknac:
 * powrot do kroku 1, skopiowanie listy dla dostawcy, zgloszenie administratorowi.
 * Slot children to miejsce na panel uprawnionego (AdminFallbackPanel).
 */
const CoverageWarning = ({
  coverage, onWrocDoKroku1, onKopiujListe, onZglos,
  moznaOdblokowac = false, children
}) => {
  const [zgloszono, setZgloszono] = useState(false);
  const [skopiowano, setSkopiowano] = useState(false);

  if (!coverage) return null;

  const brakujace = Array.isArray(coverage.brakujace) ? coverage.brakujace : [];
  const total = coverage.total != null ? coverage.total : brakujace.length;
  const brak = coverage.brak != null ? coverage.brak : brakujace.length;

  const kopiuj = () => {
    const tekst = brakujace
      .map(b => [b.kod_produktu || b.kod || '', b.ean || '', b.nazwa_produktu || b.nazwa || ''].join('\t'))
      .join('\n');
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(tekst).then(() => setSkopiowano(true)).catch(() => setSkopiowano(false));
    }
    if (onKopiujListe) onKopiujListe(tekst);
  };

  // UWAGA: to NIE wysyla powiadomienia do administratora - system nie ma takiego
  // kanalu. Przycisk kopiuje gotowa tresc zgloszenia do schowka, a napis mowi
  // wprost, ze liste trzeba przekazac samemu. Wczesniejszy komunikat "Zgloszono"
  // dawal falszywe wrazenie, ze admin juz wie.
  const zglos = () => {
    const tresc =
      'Brak pozycji w systemie (import faktury zakupowej). Wymagany cennik dostawcy albo ' +
      'uzupelnienie danych przez administratora:\n' +
      brakujace
        .map(b => `- kod: ${b.kod_produktu || b.kod || '-'}, EAN: ${b.ean || '-'}, nazwa z XML: ${b.nazwa_produktu || b.nazwa || '-'}`)
        .join('\n');
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(tresc).catch(() => {});
    }
    setZgloszono(true);
    if (onZglos) onZglos(brakujace, tresc);
  };

  // Standard UI: wypelniony = pelne tlo + #fff, niewypelniony = var(--text-secondary)
  // z kolorowa ramka (kolorowy tekst 11px na jasnym tle nie spelnia WCAG AA).
  const przyciskStyle = (kolor, wypelniony) => ({
    padding: '6px 12px', fontSize: '11px', borderRadius: '4px', cursor: 'pointer',
    border: `1px solid ${kolor}`,
    backgroundColor: wypelniony ? kolor : 'var(--card-bg)',
    color: wypelniony ? '#fff' : 'var(--text-secondary)',
    fontWeight: wypelniony ? '600' : '500'
  });

  const thStyle = { padding: '0.4rem', textAlign: 'left', borderBottom: '1px solid var(--border)', fontSize: '11px' };
  const tdStyle = { padding: '0.35rem 0.4rem', borderBottom: '1px solid var(--border)', fontSize: '12px' };

  return (
    <div style={{
      border: '1px solid var(--danger)', borderRadius: '6px',
      backgroundColor: 'var(--danger-light)', padding: '12px', marginTop: '12px'
    }}>
      <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--danger)' }}>
        {'⛔'} Faktura zawiera {brak} pozycji, kt{'ó'}rych nie ma w systemie (z {total}).
      </div>
      <div style={{ fontSize: '12px', color: 'var(--text)', marginTop: '4px' }}>
        Wgraj cennik tego dostawcy {'—'} cennik zawiera poprawne nazwy i stawki VAT.
        Bez tego pozycje nie wejd{'ą'} na stan magazynowy ani do FIFO.
      </div>

      {brakujace.length > 0 && (
        <div style={{
          marginTop: '10px', maxHeight: '200px', overflowY: 'auto',
          backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '4px'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--bg)' }}>
                <th style={thStyle}>Kod</th>
                <th style={thStyle}>EAN</th>
                <th style={thStyle}>Nazwa z XML</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Ilo{'ść'}</th>
              </tr>
            </thead>
            <tbody>
              {brakujace.map((b, i) => (
                <tr key={i}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{b.kod_produktu || b.kod || '-'}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{b.ean || '-'}</td>
                  <td style={tdStyle}>{b.nazwa_produktu || b.nazwa || '-'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{b.ilosc != null ? b.ilosc : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
        {onWrocDoKroku1 && (
          <button onClick={onWrocDoKroku1} style={przyciskStyle('var(--primary)', true)}>
            Wr{'ó'}{'ć'} do Kroku 1 i wgraj cennik
          </button>
        )}
        <button onClick={kopiuj} style={przyciskStyle('var(--info)', false)}>
          {skopiowano ? 'Skopiowano ✓' : 'Kopiuj listę'}
        </button>
        <button onClick={zglos} style={przyciskStyle('var(--text-muted)', false)}>
          Przygotuj zg{'ł'}oszenie dla administratora
        </button>
      </div>

      {zgloszono && (
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '6px' }}>
          Tre{'ść'} zg{'ł'}oszenia skopiowana do schowka. System NIE wysy{'ł'}a jej automatycznie {'—'}
          {' '}przeka{'ż'} j{'ą'} administratorowi (czat / telefon / e-mail).
        </div>
      )}

      {moznaOdblokowac && children}
    </div>
  );
};

export default CoverageWarning;
